import ActionListStatusGroup from '@/components/ActionListCompletedActions';
import ActionRow from '@/components/ActionRow';
// import AddActionButton from '@/components/AddActionButton';
import BlockListCompleted from '@/components/BlockListCompleted';
import CreateBlockModal from '@/components/CreateBlockModal';
import DayWeekNavigator from '@/components/DayWeekNavigator';
import DndContainer from '@/components/DndContainer';
import { ActionList, DroppableContainer } from '@/components/DragAndDrop';
import ActionListItem from '@/components/DragAndDrop/ActionListItem';
import DnDBlockItem from '@/components/DragAndDrop/DnDBlockItem';
import EmptyActionList from '@/components/EmptyActionList';
import MonthYearNavigator from '@/components/MonthYearNavigator';
import QuickActionCreator from '@/components/QuickActionCreator';
import UncompletedActionsModal from '@/components/UncompletedActionsModal';
import YearNavigator from '@/components/YearNavigator';
import { useCMActions, useUpdateAction } from '@/services/action/hooks';
import { useBlocks } from '@/services/block/hooks';
import {
  completeActionsOfBlock,
  onMutateBlockStatus,
  removeIncompleteActionsFromBlock,
  useCompleteBlockStatus,
  useDeleteBlock,
  useIncompleteBlockStatus,
  useRemoveActionFromBlock,
  useUpdateActionsCategoryOrder,
} from '@/services/myPlan/hooks';
import { getWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { IconArrowDownFull } from '@/theme/icons';
import { Action, RecurringWeekContext } from '@/types/actions';
import { Block } from '@/types/block';
import { Category } from '@/types/category';
import {
  getActionsByBlockId,
  getActionsByCategoryId,
  getCompletedActions,
  getDailyActions,
  getIncompleteActions,
  getWeeklyActions,
} from '@/utils/action';
import { getBlockById } from '@/utils/block';
import { appendRecurringRangeCompleteShadows } from '@/utils/recurringCompletedShadow';
import {
  endOfTheMonth,
  endOfTheWeek,
  formatStringToDate,
  getRecurringInstancesInRangeCompletionSummary,
  hasRecurringOccurrenceInRange,
  isThisMonth,
  isThisWeek,
  isThisYear,
  startOfTheMonth,
  startOfTheWeek,
  utcToLocalDate,
} from '@/utils/calendar';
import rem from '@/utils/rem';
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DragEndEvent, DragOverlay, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { endOfDay, endOfYear, isToday, startOfDay, startOfYear } from 'date-fns';
import { useAnimation } from 'framer-motion';
import { isEmpty, orderBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface ActionsSectionProps {
  category: Category;
}

type PeriodType = 'day' | 'week' | 'month' | 'year';

const ActionsSection: React.FC<ActionsSectionProps> = ({ category }) => {
  const { t } = useTranslation('CategorySpotlight');
  const toast = useToast();

  // Period selector state
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('week');

  // DnD state
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  // Quick action creator state
  const actionCreatorOptionsAnimation = useAnimation();
  const [animationState, setAnimationState] = useState<'visible' | 'exit'>('exit');

  const handleAnimationStateChange = useCallback((state: 'visible' | 'exit') => {
    setAnimationState(state);
  }, []);

  // Block state management
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);

  const {
    isOpen: isOpenSetUncompletedActionsModal,
    onOpen: openSetUncompletedActionsModal,
    onClose: onCloseSetUncompletedActionsModal,
  } = useDisclosure();

  const {
    isOpen: isOpenCreateBlockModal,
    onOpen: openCreateBlockModal,
    onClose: onCloseCreateBlockModal,
  } = useDisclosure();

  // Calendar and data hooks
  const { selectedDate, updateSelectedDate } = useCalendarMonthlyStore();

  // Get all category actions once
  const { data: categoryActions, isLoading: isLoadingCategoryActions } = useCMActions(category.id);

  const { data: blocks } = useBlocks();

  // Filter actions based on selected period
  const filteredActions = useMemo(() => {
    if (!categoryActions?.action) return { action: [] };

    const allActions = categoryActions.action;

    switch (selectedPeriod) {
      case 'day': {
        const base = getDailyActions(allActions, selectedDate);
        const rangeStart = startOfDay(selectedDate);
        const rangeEnd = endOfDay(selectedDate);
        const seen = new Set(base.map((a) => a.id));
        const recurringInDay = allActions.filter(
          (a) => !a.isCanceled && !seen.has(a.id) && hasRecurringOccurrenceInRange(a, rangeStart, rangeEnd),
        );
        return { action: [...base, ...recurringInDay] };
      }
      case 'week': {
        const weeklyPlanId = getWeeklyPlanId(selectedDate);
        const base = weeklyPlanId ? getWeeklyActions(allActions, weeklyPlanId) : [];
        const rangeStart = startOfTheWeek(selectedDate);
        const rangeEnd = endOfTheWeek(selectedDate);
        const seen = new Set(base.map((a) => a.id));
        const recurringInWeek = allActions.filter(
          (a) => !a.isCanceled && !seen.has(a.id) && hasRecurringOccurrenceInRange(a, rangeStart, rangeEnd),
        );
        return { action: [...base, ...recurringInWeek] };
      }
      case 'month':
      case 'year': {
        const filterByPeriod = (action: Action) => {
          let actionDate: Date | null = null;

          if (action.scheduledDate) {
            actionDate = utcToLocalDate(action.scheduledDate, action.scheduledTime, action.timezone, action.gmtOffset);
          } else if (action.weekly_plan?.dateFrom) {
            actionDate = formatStringToDate(action.weekly_plan.dateFrom);
          }

          if (!actionDate) return false;

          if (selectedPeriod === 'month') {
            return (
              actionDate.getFullYear() === selectedDate.getFullYear() &&
              actionDate.getMonth() === selectedDate.getMonth()
            );
          }
          return actionDate.getFullYear() === selectedDate.getFullYear();
        };

        const rangeStart =
          selectedPeriod === 'month' ? startOfTheMonth(selectedDate) : startOfYear(selectedDate);
        const rangeEnd = selectedPeriod === 'month' ? endOfTheMonth(selectedDate) : endOfYear(selectedDate);

        const base = allActions.filter(filterByPeriod);
        const seen = new Set(base.map((a) => a.id));
        const recurringInRange = allActions.filter(
          (a) => !a.isCanceled && !seen.has(a.id) && hasRecurringOccurrenceInRange(a, rangeStart, rangeEnd),
        );

        return { action: [...base, ...recurringInRange] };
      }
      default:
        return { action: [] };
    }
  }, [categoryActions, selectedPeriod, selectedDate]);

  const spotlightRecurringContext = useMemo((): RecurringWeekContext => {
    switch (selectedPeriod) {
      case 'day':
        return {
          weekStart: startOfDay(selectedDate),
          weekEnd: endOfDay(selectedDate),
          granularity: 'day',
        };
      case 'month':
        return {
          weekStart: startOfTheMonth(selectedDate),
          weekEnd: endOfTheMonth(selectedDate),
          granularity: 'month',
        };
      case 'year':
        return {
          weekStart: startOfYear(selectedDate),
          weekEnd: endOfYear(selectedDate),
          granularity: 'year',
        };
      case 'week':
      default:
        return {
          weekStart: startOfTheWeek(selectedDate),
          weekEnd: endOfTheWeek(selectedDate),
          granularity: 'week',
        };
    }
  }, [selectedPeriod, selectedDate]);

  const isLoading = isLoadingCategoryActions;

  // Period check logic
  const isCurrentPeriod = useMemo(() => {
    switch (selectedPeriod) {
      case 'day':
        return isToday(selectedDate);
      case 'month':
        return isThisMonth(selectedDate);
      case 'year':
        return isThisYear(selectedDate);
      default:
        return isThisWeek(startOfTheWeek(selectedDate));
    }
  }, [selectedPeriod, selectedDate]);

  // Mutations
  const deleteBlock = useDeleteBlock();
  const updateActionsCategoryOrder = useUpdateActionsCategoryOrder();
  const updateActionCategory = useUpdateAction({
    onError: (error): void => {
      toast({
        title: error?.response?.errors?.[0]?.message || t('actions.updateError'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  // Block mutation hooks
  const removeActionFromBlock = useRemoveActionFromBlock();
  const completeBlockStatus = useCompleteBlockStatus({
    onMutate: ({ id }) => onMutateBlockStatus(id, true),
  });
  const incompleteBlockStatus = useIncompleteBlockStatus({
    onMutate: ({ id }) => onMutateBlockStatus(id),
  });

  // Get sorted actions for the selected category
  const getSortedActionsByCategoryId = useCallback(
    (categoryId: string) => {
      if (!filteredActions?.action) {
        return [];
      }

      const categoryActions = getActionsByCategoryId(filteredActions.action, categoryId);

      let response = categoryActions;

      response = response.map((action) => ({
        ...action,
        block: action?.blockId && blocks?.block ? (getBlockById(blocks.block, action.blockId) ?? null) : null,
      }));

      return orderBy(response, 'categoryOrder', 'asc');
    },
    [filteredActions?.action, blocks?.block],
  );

  // Get category actions with completion status
  const categoryWithActions = useMemo(() => {
    const categoryActions = getSortedActionsByCategoryId(category.id);

    const recurringCompletionRange = (() => {
      switch (selectedPeriod) {
        case 'day':
          return { start: startOfDay(selectedDate), end: endOfDay(selectedDate) };
        case 'week':
          return { start: startOfTheWeek(selectedDate), end: endOfTheWeek(selectedDate) };
        case 'month':
          return { start: startOfTheMonth(selectedDate), end: endOfTheMonth(selectedDate) };
        case 'year':
          return { start: startOfYear(selectedDate), end: endOfYear(selectedDate) };
      }
    })();

    let incompleteActionsFiltered = getIncompleteActions(categoryActions).filter(
      (a) => !a.project?.isArchived && !a.isCanceled,
    );

    incompleteActionsFiltered = incompleteActionsFiltered.filter((a) => {
      if (!a?.event?.recurrence?.rules?.length) return true;
      const summary = getRecurringInstancesInRangeCompletionSummary(
        a,
        recurringCompletionRange.start,
        recurringCompletionRange.end,
      );
      if (summary.inRangeCount > 0 && summary.allCompleteInRange) return false;
      return true;
    });

    let completedActionsFiltered = getCompletedActions(categoryActions, 'modifiedAt').filter(
      (a) => !a.project?.isArchived && !a.isCanceled,
    );

    completedActionsFiltered = appendRecurringRangeCompleteShadows(
      completedActionsFiltered,
      categoryActions,
      recurringCompletionRange.start,
      recurringCompletionRange.end,
    );

    return {
      ...category,
      incompleteActions: incompleteActionsFiltered,
      completedActions: completedActionsFiltered,
      canceledActions: getActionsByCategoryId(
        categoryActions.filter((a) => a.isCanceled),
        category.id,
      ),
    };
  }, [category, getSortedActionsByCategoryId, selectedPeriod, selectedDate]);

  // Block filtering logic
  const filteredBlocks = useMemo(() => {
    if (!blocks?.block || !filteredActions?.action) {
      return [];
    }

    return orderBy(
      blocks.block.filter((block) => {
        const blockActions = getActionsByBlockId(block.id, filteredActions.action);
        return blockActions.length > 0 && block.categoryId === category.id;
      }),
      'weeklyOrder',
      'asc',
    );
  }, [blocks?.block, filteredActions?.action, category.id]);

  const [completedBlocks, notCompletedBlocks] = useMemo(() => {
    const completed = filteredBlocks.filter((block) => block.isCompleted) || [];
    const notCompleted = filteredBlocks.filter((block) => !block.isCompleted) || [];

    return [completed, notCompleted];
  }, [filteredBlocks]);

  // Separate actions that are in blocks vs not in blocks
  const actionsNotInBlocks = useMemo(() => {
    return {
      incompleteActions: categoryWithActions.incompleteActions.filter((action) => !action.blockId),
      completedActions: categoryWithActions.completedActions.filter((action) => !action.blockId),
      canceledActions: categoryWithActions.canceledActions.filter((action) => !action.blockId),
    };
  }, [categoryWithActions]);

  // Block handlers
  const onCompleteBlock = useCallback(
    (blockId: string) => {
      completeBlockStatus.mutate({
        id: blockId,
      });
    },
    [completeBlockStatus],
  );

  const onUncompleteBlock = useCallback(
    (blockId: string) => {
      if (!blocks?.block) {
        return;
      }

      const block = getBlockById(blocks.block, blockId);

      if (!block) {
        return;
      }

      incompleteBlockStatus.mutate({
        id: blockId,
      });
    },
    [blocks?.block, incompleteBlockStatus],
  );

  const onCompleteBlockActions = useCallback(() => {
    if (!selectedBlock) {
      return;
    }

    if (selectedBlock.actions) {
      const incompleteActions = getIncompleteActions(selectedBlock.actions);
      // You can add action tracking here if needed
    }

    category.id && completeActionsOfBlock(category.id);
    setSelectedBlock(null);
  }, [selectedBlock, category.id]);

  const onRemoveBlockActions = useCallback(() => {
    if (!selectedBlock) {
      return;
    }

    removeIncompleteActionsFromBlock(selectedBlock);
    setSelectedBlock(null);
  }, [selectedBlock]);

  const handleRemoveActionFromBlock = useCallback(
    (actionId: string, blockId: string) => {
      if (!blocks?.block) {
        return;
      }

      const block = getBlockById(blocks.block, blockId);

      if (!block) {
        return;
      }

      removeActionFromBlock.mutate({
        actionId,
        categoryId: block.categoryId,
        projectId: block.projectId,
      });
    },
    [blocks?.block, removeActionFromBlock],
  );

  const handleDeleteBlock = useCallback(
    (block: Block | undefined) => {
      if (!block) {
        return;
      }

      setSelectedBlock(block);
      // You can add delete modal logic here if needed
      deleteBlock.mutate({ blockId: block.id });
    },
    [deleteBlock],
  );

  // Empty handlers for block modals (not implemented in CategorySpotlight)
  const openEditBlockModal = useCallback(() => {
    // Not implemented for CategorySpotlight
  }, []);

  const openDuplicateBlockModal = useCallback(() => {
    // Not implemented for CategorySpotlight
  }, []);

  // DnD handlers
  const onDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
    setActiveItemIndex(active?.data?.current?.index);
  };

  const updateCaptureListActionsOrder = useCallback(
    (actions: Action[]) => {
      const mutationPayload = actions.map((action, index) => ({
        _set: {
          categoryOrder: index + 1,
        },
        where: {
          id: {
            _eq: action.id,
          },
        },
      }));

      updateActionsCategoryOrder.mutate(mutationPayload);
    },
    [updateActionsCategoryOrder],
  );

  const handleActionNewCategory = useCallback(
    (actionId: string, categoryId: string, blockId: string | null) => {
      const actionsByCategory = getActionsByCategoryId(filteredActions?.action ?? [], categoryId);

      let shouldDeleteBlock = false;

      if (blockId) {
        const blockActions = filteredActions?.action?.filter((action: Action) => action.blockId === blockId) ?? [];
        if (blockActions?.length === 1) {
          shouldDeleteBlock = true;
        }
      }

      updateActionCategory.mutate({
        actionId,
        set: { categoryId, categoryOrder: actionsByCategory.length + 1, blockId: null, blockOrder: null },
        showSyncEvent: false,
      });

      if (shouldDeleteBlock && blockId) {
        deleteBlock.mutate({ blockId });
      }
    },
    [filteredActions?.action, deleteBlock, updateActionCategory],
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const overId = over?.id;

    if (overId == null) {
      setActiveId(null);
      setActiveItemIndex(0);
      return;
    }

    const activeIndex = active?.data?.current?.index;
    const overIndex = over?.data?.current?.index;
    const activeContainerId = active?.data?.current?.containerId;
    const overContainerId = over?.data?.current?.containerId;
    const overCategoryId = over?.data?.current?.categoryId;
    const activeBlockId = active?.data?.current?.blockId;

    if (activeContainerId === overContainerId) {
      const actions = getIncompleteActions(getSortedActionsByCategoryId(active?.data?.current?.categoryId));
      updateCaptureListActionsOrder(arrayMove(actions, activeIndex, overIndex));
    }

    if (activeContainerId !== overContainerId) {
      handleActionNewCategory(active.id.toString(), overCategoryId, activeBlockId);
    }

    setActiveId(null);
    setActiveItemIndex(0);
  };

  const onDragCancel = () => {
    setActiveId(null);
    setActiveItemIndex(0);
  };

  // Drag overlay
  const ItemOverlay = useMemo(() => {
    if (!activeId || !filteredActions?.action) {
      return <></>;
    }

    const item = filteredActions.action.find((action: Action) => action.id === activeId.toString());

    if (!item) {
      return <></>;
    }

    return createPortal(
      <DragOverlay dropAnimation={{ easing: 'ease-in' }}>
        <ActionRow
          action={item}
          idx={activeItemIndex + 1}
          disableRecurringStatusChanges
          recurringWeekContext={spotlightRecurringContext}
        />
      </DragOverlay>,
      document.body,
    );
  }, [filteredActions?.action, activeId, activeItemIndex, spotlightRecurringContext]);

  // Period title and empty state logic
  const getPeriodTitle = () => {
    return t(`actions.periodSelector.${selectedPeriod}`);
  };

  const getEmptyStateMessage = () => {
    return isEmpty(categoryWithActions?.completedActions) && filteredBlocks.length === 0
      ? t(`actions.emptyStates.${selectedPeriod}`)
      : t(`actions.allCompletedStates.${selectedPeriod}`);
  };

  const getCurrentPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'day':
        return isCurrentPeriod ? t('actions.currentPeriod.currentDay') : t('actions.currentPeriod.currentDay');
      case 'month':
        return isCurrentPeriod ? t('actions.currentPeriod.currentMonth') : t('actions.currentPeriod.currentMonth');
      case 'year':
        return isCurrentPeriod ? t('actions.currentPeriod.currentYear') : t('actions.currentPeriod.currentYear');
      default:
        return isCurrentPeriod ? t('actions.currentPeriod.currentWeek') : t('actions.currentPeriod.currentWeek');
    }
  };

  // Render navigator based on selected period
  const renderNavigator = () => {
    switch (selectedPeriod) {
      case 'day':
        return (
          <DayWeekNavigator
            selectedDate={selectedDate}
            showCalendarIcon
            showDatepicker
            onCategorySpotlight
            isInDayMode
          />
        );
      case 'month':
        return (
          <MonthYearNavigator
            selectedDate={selectedDate}
            updateSelectedDate={updateSelectedDate}
            showDatepicker
            onCategorySpotlight
          />
        );
      case 'year':
        return (
          <YearNavigator
            selectedDate={selectedDate}
            updateSelectedDate={updateSelectedDate}
            showDatepicker
            onCategorySpotlight
          />
        );
      default:
        return <DayWeekNavigator selectedDate={selectedDate} showCalendarIcon showDatepicker onCategorySpotlight />;
    }
  };

  if (isLoading) {
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        minHeight="fit-content"
        padding={rem(24)}
        backgroundColor="background-primary"
      >
        <Spinner size="lg" speed="0.65s" thickness={rem(3)} />
      </Flex>
    );
  }

  return (
    <DndContainer onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
      <Flex flexDirection="column" minHeight="fit-content" padding={rem(24)} backgroundColor="background-primary">
        {/* Header */}
        <Flex alignItems="center" gap={rem(12)} marginBottom={rem(20)}>
          <Text as="h4" textStyle="h4BarSB" color="white" fontSize={rem(28)} textTransform="uppercase">
            {t('actions.title')} {selectedPeriod === 'day' && `${t('actions.periodSelector.day').split(' ')[1]}`}
            {selectedPeriod === 'week' && `${t('actions.periodSelector.week').split(' ')[1]}`}
            {selectedPeriod === 'month' && `${t('actions.periodSelector.month').split(' ')[1]}`}
            {selectedPeriod === 'year' && `${t('actions.periodSelector.year').split(' ')[1]}`}
          </Text>
        </Flex>

        <Flex alignItems="center" justifyContent="space-between" height={rem(33)}>
          {renderNavigator()}

          <Menu matchWidth>
            <MenuButton
              as={Button}
              sx={{
                'span:first-of-type': { overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
              justifyContent="space-between"
              display="flex"
              width={rem(120)}
              fontSize="md"
              textAlign="left"
              rightIcon={<Icon as={IconArrowDownFull} boxSize={rem(7)} />}
              variant="secondary"
            >
              {getPeriodTitle()}
            </MenuButton>
            <MenuList
              sx={{
                // Style of the menu items
                button: {
                  justifyContent: 'space-between',
                  background: 'none',
                  display: 'flex',
                  _hover: { backgroundColor: 'background-tertiary' },
                  'span:first-of-type': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  },
                  'span:last-of-type': {
                    flexShrink: 0,
                    width: rem(20),
                    textAlign: 'right',
                  },
                },
              }}
              zIndex={10}
              padding={rem(8)}
              fontSize="md"
              borderWidth={rem(1)}
              borderStyle="solid"
              borderColor="gray.700"
              borderRadius="4"
              backgroundColor="background-primary"
            >
              <MenuItem onClick={() => setSelectedPeriod('day')}>
                <Box as="span">{t('actions.periodSelector.day')}</Box>
                <Box as="span">{selectedPeriod === 'day' && '✓'}</Box>
              </MenuItem>
              <MenuItem onClick={() => setSelectedPeriod('week')}>
                <Box as="span">{t('actions.periodSelector.week')}</Box>
                <Box as="span">{selectedPeriod === 'week' && '✓'}</Box>
              </MenuItem>
              <MenuItem onClick={() => setSelectedPeriod('month')}>
                <Box as="span">{t('actions.periodSelector.month')}</Box>
                <Box as="span">{selectedPeriod === 'month' && '✓'}</Box>
              </MenuItem>
              <MenuItem onClick={() => setSelectedPeriod('year')}>
                <Box as="span">{t('actions.periodSelector.year')}</Box>
                <Box as="span">{selectedPeriod === 'year' && '✓'}</Box>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>

        {/* Quick Action Creator */}
        <Box
          flexShrink={0}
          height={animationState === 'visible' ? 'auto' : rem(100)}
          marginTop={rem(22)}
          marginBottom={rem(22)}
          padding={rem(16)}
          paddingTop={rem(26)}
          border="1px solid"
          borderColor="whiteAlpha.300"
          borderRadius={rem(4)}
        >
          <QuickActionCreator
            category={categoryWithActions}
            selectedCategoryId={category?.id}
            actionCreatorOptionsAnimation={actionCreatorOptionsAnimation}
            shouldFocus={false}
            defaultToDayMode={selectedPeriod === 'day'}
            onAnimationStateChange={handleAnimationStateChange}
          />
        </Box>

        {/* Actions Content */}
        <VStack alignItems="stretch" flex="1" spacing={rem(16)}>
          {isCurrentPeriod && (
            <Text textStyle="hSatMedium" color="text-primary" fontWeight={500}>
              {getCurrentPeriodLabel()}
            </Text>
          )}
          {/* Actions List */}
          <DroppableContainer
            id={category?.id}
            payload={{
              categoryId: category?.id,
            }}
            as={VStack}
            alignItems="flex-start"
            width="full"
          >
            <SortableContext items={actionsNotInBlocks?.incompleteActions}>
              <ActionList
                width="full"
                emptyListComponent={
                  <EmptyActionList
                    title={getEmptyStateMessage()}
                    message={isEmpty(actionsNotInBlocks?.completedActions) ? t('actions.clickToAdd') : undefined}
                    motivational="small"
                  />
                }
                isEmpty={isEmpty(actionsNotInBlocks?.incompleteActions)}
              >
                {actionsNotInBlocks?.incompleteActions.map((action, index) => (
                  <ActionListItem
                    key={action.id}
                    item={action}
                    index={index}
                    dndPayload={{
                      containerId: `captureList-${category?.id}`,
                      categoryId: action.categoryId,
                      category: categoryWithActions,
                      index,
                      projectId: action?.projectId,
                      blockId: action?.blockId,
                    }}
                    isCompleteMode={false}
                    disableRecurringStatusChanges
                    recurringWeekContext={spotlightRecurringContext}
                  />
                ))}
              </ActionList>
            </SortableContext>
          </DroppableContainer>

          {/* Completed Actions */}
          <ActionListStatusGroup
            actions={actionsNotInBlocks.completedActions}
            title={t('actions.completedActions')}
            emptyMessage={t('actions.noCompletedActions')}
            recurringWeekContext={spotlightRecurringContext}
          />

          {/* Blocks Section */}
          {(notCompletedBlocks.length > 0 || completedBlocks.length > 0) && (
            <VStack alignItems="stretch" width="full" marginTop={rem(24)} spacing={rem(16)}>
              <HStack justifyContent="space-between" width="full" minHeight={rem(30)}>
                <Text textStyle="hSatMedium" color="text-primary" fontWeight={500}>
                  {t('actions.blocks')}
                </Text>

                {/* <AddActionButton
                  tooltipText={t('actions.addNewBlock')}
                  onClick={openCreateBlockModal}
                  variant="secondary"
                  width={rem(28)}
                  height={rem(28)}
                /> */}
              </HStack>

              {/* Incomplete Blocks */}
              {notCompletedBlocks.map((block, index) => (
                <DnDBlockItem
                  key={block.id}
                  actions={getActionsByBlockId(block.id, filteredActions?.action ?? [])}
                  block={block}
                  index={index}
                  weeklyPlanId=""
                  onCompleteBlock={onCompleteBlock}
                  openSetUncompletedActionsModal={openSetUncompletedActionsModal}
                  onUncompleteBlock={onUncompleteBlock}
                  onSelectBlock={() => setSelectedBlock(block)}
                  onClickRemoveFromBlock={handleRemoveActionFromBlock}
                  withScheduleButton
                  onDeleteBlock={handleDeleteBlock}
                  openEditBlockModal={openEditBlockModal}
                  openDuplicateBlockModal={openDuplicateBlockModal}
                  collapsedByDefault={true}
                  disableRecurringStatusChanges
                  recurringWeekContext={spotlightRecurringContext}
                />
              ))}

              {/* Completed Blocks */}
              <BlockListCompleted
                blocks={completedBlocks}
                allActionsOrderedByCreatedAt={filteredActions?.action ?? []}
                onCompleteBlock={onCompleteBlock}
                openSetUncompletedActionsModal={openSetUncompletedActionsModal}
                onUncompleteBlock={onUncompleteBlock}
                onSelectedBlock={setSelectedBlock}
                recurringWeekContext={spotlightRecurringContext}
                onClickRemoveFromBlock={handleRemoveActionFromBlock}
                onDeleteBlock={handleDeleteBlock}
                openEditBlockModal={openEditBlockModal}
                openDuplicateBlockModal={openDuplicateBlockModal}
                collapsedByDefault={true}
                disableRecurringStatusChanges
              />
            </VStack>
          )}
        </VStack>
      </Flex>

      {ItemOverlay}

      {/* Block Modals */}
      {isOpenSetUncompletedActionsModal && selectedBlock && (
        <UncompletedActionsModal
          blockId={selectedBlock?.id ?? ''}
          completedActions={selectedBlock?.actions?.filter((action) => action.progressStatus === 'complete') ?? []}
          isOpen={isOpenSetUncompletedActionsModal}
          onClose={onCloseSetUncompletedActionsModal}
          onDeleteBlock={({ id }) => deleteBlock.mutate({ blockId: id })}
          onCompleteBlockActions={onCompleteBlockActions}
          onRemoveBlockActions={onRemoveBlockActions}
          onCompleteBlock={onCompleteBlock}
        />
      )}

      {isOpenCreateBlockModal && (
        <CreateBlockModal
          isOpen={isOpenCreateBlockModal}
          onClose={onCloseCreateBlockModal}
          categoryId={category?.id}
          scheduledDate={selectedDate}
          filterPeriod={selectedPeriod}
          readyOnlyCategory
        />
      )}
    </DndContainer>
  );
};

export default ActionsSection;
